import { z } from 'zod';
import { connectionStatusSchema, storeCodeSchema, storeModeSchema } from '../status/store.js';
import { isoDateTimeSchema, objectIdSchema, paginationQuerySchema, sortQuerySchema } from './common.js';
export const blockingProductSchema = z.object({
    productId: z.string().min(1),
    title: z.string(),
});
/** Produit « Mot personnalisé » (`01` §2.2.e, `03` §2 `stores.customMessage`). */
export const customMessageConfigSchema = z.object({
    productId: z.string().min(1),
    propertyName: z.string().min(1),
    column: z.union([z.literal(7), z.literal(8)]),
});
export const storeConnectionSchema = z.object({
    status: connectionStatusSchema,
    grantedScopes: z.array(z.string()),
    missingScopes: z.array(z.string()),
    installedAt: isoDateTimeSchema.nullable(),
    uninstalledAt: isoDateTimeSchema.nullable(),
});
export const storeListItemSchema = z.object({
    id: objectIdSchema,
    code: storeCodeSchema,
    name: z.string(),
    shopDomain: z.string(),
    currency: z.string().nullable(),
    mode: storeModeSchema,
    connection: storeConnectionSchema,
    lastWebhookAt: isoDateTimeSchema.nullable(),
    lastPollAt: isoDateTimeSchema.nullable(),
});
export const storeDetailSchema = z.object({
    ...storeListItemSchema.shape,
    exportFrom: isoDateTimeSchema.nullable(),
    offsetMinutes: z.number().int().min(1).max(120),
    webhookSilenceHours: z.number().int().min(1).nullable(),
    blockingProductIds: z.array(blockingProductSchema),
    customMessage: customMessageConfigSchema.nullable(),
    defaultCarrier: z.object({ code: z.string(), label: z.string() }),
});
export const storeUpdateBodySchema = z.object({
    /**
     * Le code est modifiable tant qu'aucun objet ne s'y rattache (`02` §12.1) :
     * l'API répond 409 sinon.
     */
    code: storeCodeSchema.optional(),
    name: z.string().min(1).optional(),
    mode: storeModeSchema.optional(),
    exportFrom: isoDateTimeSchema.nullable().optional(),
    offsetMinutes: z.number().int().min(1).max(120).optional(),
    webhookSilenceHours: z.number().int().min(1).nullable().optional(),
    blockingProductIds: z.array(blockingProductSchema).optional(),
    customMessage: customMessageConfigSchema.nullable().optional(),
    defaultCarrier: z.object({ code: z.string(), label: z.string() }).optional(),
});
/** Carte boutique du tableau de bord (`02` §2). */
export const storeDashboardCardSchema = z.object({
    ...storeListItemSchema.shape,
    todayCounts: z.object({
        received: z.number().int().min(0),
        sent: z.number().int().min(0),
        error: z.number().int().min(0),
        blocked: z.number().int().min(0),
    }),
    nextSendAt: isoDateTimeSchema.nullable(),
    alert: z.boolean(),
});
/** Recherche de produits Shopify pour les réglages boutique (`02` §12). */
export const shopifyProductSearchQuerySchema = z.object({
    store: storeCodeSchema,
    query: z.string().min(1),
});
export const shopifyProductSchema = z.object({
    productId: z.string(),
    title: z.string(),
    handle: z.string(),
});
export const shopifyProductSearchResponseSchema = z.object({
    items: z.array(shopifyProductSchema),
});
/** `GET /api/stores` (`02` §12.2) : liste triable, non filtrée (peu de boutiques). */
export const storeListQuerySchema = z.object({
    ...paginationQuerySchema.shape,
    ...sortQuerySchema.shape,
});
/**
 * `GET /auth/shopify/install` (`04` §4.3) : redirige vers Shopify, ne renvoie
 * jamais de JSON en usage normal — ce schéma documente les seuls paramètres
 * hostiles avant construction de l'URL.
 *
 * `shop` est la forme retenue depuis que les boutiques sont créées au callback
 * OAuth (décision du 10/09/2026) : l'utilisateur saisit un domaine, pas un
 * code qui n'existe pas encore. `store` reste accepté pour les liens existants
 * (réinstallation d'une boutique déjà connue).
 */
export const storeInstallQuerySchema = z
    .object({
    shop: z
        .string()
        .regex(/^[a-z0-9-]+\.myshopify\.com$/i, 'domaine `xxx.myshopify.com` attendu')
        .optional(),
    store: storeCodeSchema.optional(),
})
    .refine((query) => query.shop !== undefined || query.store !== undefined, {
    message: 'shop ou store requis',
    path: ['shop'],
});
//# sourceMappingURL=stores.js.map